import type { TrackerKpi, TrackerPillar, TrackerTarget, TrackerValue, Unit, Week } from 'chery-contracts';
import {
  applicableTarget,
  BLANK_DISPLAY,
  completionActual,
  completionBasis,
  computeCompletion,
  coveredWindow,
  formatCompletion,
  formatValue,
  isNumericUnit,
  MOVEMENT_GLYPHS,
  parseInputForUnit,
  PILLAR_META,
  weekOverWeek,
  type RangeSummary,
} from 'chery-kpi-engine';
import { formatDayMonth } from '../system/system-display';
import type { CellEdit, CellRecord, CellValue, SelectionTotal } from './tracker-state';

/**
 * Presentation rules of the tracker grid (plan §7) — pure, so the cell text, movement glyphs, tooltips and the
 * week-convention banner are unit-tested. Display rounding is one decimal everywhere (`formatValue`); full
 * precision stays in the data.
 */

type UnitOf = Pick<TrackerKpi, 'unit' | 'currency'>;

/** The saved cell of a KPI as the editing state sees it: a Text KPI's value is its text. */
export const baseRecord = (kpi: Pick<TrackerKpi, 'unit'>, stored: TrackerValue | undefined): CellRecord | null =>
  stored
    ? {
        value: kpi.unit === 'TEXT' ? stored.textValue : stored.value,
        comment: stored.comment,
        reason: stored.reason,
        followUp: stored.followUp,
      }
    : null;

/** `1,450.0`, `12.5%`, `JOD 1,450.0`, the text of a Text KPI, or `—` for a blank. */
export const cellText = (kpi: UnitOf, value: CellValue): string => {
  if (value === null || value === '') {
    return BLANK_DISPLAY;
  }

  if (typeof value === 'string') {
    return value;
  }

  return isNumericUnit(kpi.unit) ? formatValue(kpi.unit, value, kpi.currency) : String(value);
};

/** What a typed or pasted string means for a KPI of `unit`: a value, a blank (clear), or an invalid entry. */
export const parseCellInput = (unit: Unit, raw: string): CellEdit => {
  const parsed = parseInputForUnit(unit, raw);

  if (!parsed.ok) {
    return { invalid: raw.trim() };
  }

  switch (parsed.kind) {
    case 'blank':
      return { value: null };
    case 'number':
      return { value: parsed.value };
    case 'text':
      return { value: parsed.text };
  }
};

/** The text the cell editor starts with: the raw value, never the formatted one (`1450`, not `1,450.0`). */
export const editorText = (value: CellValue, invalid: string | undefined): string => invalid ?? (value === null ? '' : String(value));

export interface MovementDisplay {
  glyph: string;
  title: string;
}

const signed = (text: string, value: number): string => (value > 0 ? `+${text}` : text);

/**
 * The neutral week-over-week glyph of a cell and its tooltip, or `null` when there is nothing to compare
 * (either week blank — never a comparison with zero).
 */
export const movementDisplay = (
  kpi: UnitOf & Pick<TrackerKpi, 'name'>,
  current: { label: string; value: CellValue },
  previous: { label: string; value: CellValue } | null,
): MovementDisplay | null => {
  if (!previous || !isNumericUnit(kpi.unit)) {
    return null;
  }

  const movement = weekOverWeek(current.value, previous.value);

  if (!movement) {
    return null;
  }

  const unit = kpi.unit;
  const pct = movement.changePct === null ? '' : ` · ${signed(`${formatValue('DECIMAL', movement.changePct)}%`, movement.changePct)}`;

  return {
    glyph: MOVEMENT_GLYPHS[movement.direction],
    title:
      `${kpi.name} · ${previous.label}: ${formatValue(unit, previous.value as number, kpi.currency)} → ${current.label}: ` +
      `${formatValue(unit, current.value as number, kpi.currency)} · change ${signed(formatValue(unit, movement.change, kpi.currency), movement.change)}${pct}`,
  };
};

export interface SummaryTexts {
  total: string;
  average: string;
  latest: string;
}

export const summaryTexts = (kpi: UnitOf, summary: RangeSummary): SummaryTexts => {
  const text = (value: number | null): string => (isNumericUnit(kpi.unit) ? formatValue(kpi.unit, value, kpi.currency) : BLANK_DISPLAY);

  return { total: text(summary.total), average: text(summary.average), latest: text(summary.latest) };
};

/** `4 Oct` over `W40` in the column header. */
export const weekColumnLabel = (week: Pick<Week, 'weekDate' | 'weekNumber'>): { date: string; number: string } => ({
  date: formatDayMonth(week.weekDate),
  number: `W${String(week.weekNumber).padStart(2, '0')}`,
});

/** `4 Oct 2026` — the reporting Sunday a column is dated by. */
export const weekDateLabel = (weekDate: string): string => `${formatDayMonth(weekDate)} ${weekDate.slice(0, 4)}`;

/** `27 Sep – 3 Oct`, or `4–10 Oct` when the seven days sit in one month. */
export const coveredWindowLabel = (weekDate: string): string => {
  const { from, to } = coveredWindow(weekDate);

  return from.slice(0, 7) === to.slice(0, 7)
    ? `${Number(from.slice(8, 10))}–${formatDayMonth(to)}`
    : `${formatDayMonth(from)} – ${formatDayMonth(to)}`;
};

/** The live example of the week-convention banner: the column dated `11 Oct` covers `4–10 Oct`. */
export const conventionExample = (weekDate: string): { column: string; covers: string } => ({
  column: formatDayMonth(weekDate),
  covers: coveredWindowLabel(weekDate),
});

/** Status-bar total of the selected cells. */
export const selectionTotalText = (total: SelectionTotal): string => (total.sum === null ? BLANK_DISPLAY : formatValue('DECIMAL', total.sum));

/** Hover text of a cell: KPI · week · its notes. */
export const cellTitle = (kpiName: string, weekDate: string, record: CellRecord): string =>
  [
    `${kpiName} · ${weekDateLabel(weekDate)}`,
    record.comment ? `Comment: ${record.comment}` : null,
    record.reason ? `Reason: ${record.reason}` : null,
    record.followUp ? `Follow-up: ${record.followUp}` : null,
  ]
    .filter(Boolean)
    .join('\n');

/** Hover text of a KPI name: code, description, method and data source. */
export const kpiTitle = (kpi: Pick<TrackerKpi, 'code' | 'name' | 'description' | 'measurementMethod' | 'dataSource' | 'pillar'>): string =>
  [
    `${kpi.name} (${kpi.code}) · ${PILLAR_META[kpi.pillar].label}`,
    kpi.description,
    kpi.measurementMethod ? `Method: ${kpi.measurementMethod}` : null,
    kpi.dataSource ? `Source: ${kpi.dataSource}` : null,
  ]
    .filter(Boolean)
    .join('\n');

/** Case-insensitive match on name, code or description. */
export const matchesSearch = (kpi: Pick<TrackerKpi, 'name' | 'code' | 'description'>, search: string): boolean => {
  const query = search.trim().toLowerCase();

  return query === '' || [kpi.name, kpi.code, kpi.description ?? ''].some((text) => text.toLowerCase().includes(query));
};

/** `Edit: all pillars`, `Edit: Selling`, or `View only`. */
export const editAccessNote = (pillars: readonly TrackerPillar[]): string => {
  const editable = pillars.filter((each) => each.access === 'EDIT');

  if (editable.length === 0) {
    return 'View only';
  }

  return editable.length === 5 ? 'Edit: all pillars' : `Edit: ${editable.map((each) => PILLAR_META[each.pillar].shortLabel).join(', ')}`;
};

/** Hover text of a calculated cell: the week, the formula, and why it cannot be typed into. */
export const calculatedCellTitle = (kpi: Pick<TrackerKpi, 'name' | 'formulaPreview' | 'restricted'>, weekDate: string): string =>
  kpi.restricted
    ? `${kpi.name} · ${weekDateLabel(weekDate)}\nRestricted — calculated from KPIs in a pillar you cannot view, so it is blank for you.`
    : `${kpi.name} · ${weekDateLabel(weekDate)}\nƒ = ${kpi.formulaPreview ?? '—'}\nCalculated — read-only; it recalculates as its sources change.`;

const DIRECTION_TEXT: Readonly<Record<TrackerTarget['direction'], string>> = { AT_LEAST: 'at least', AT_MOST: 'at most' };

const BASIS_TEXT = { TOTAL: 'range Total', AVERAGE: 'range Average', LATEST: 'Latest value' } as const;

export interface TargetDisplay {
  /** `800.0`, `JOD 20,000,000.0`. */
  value: string;
  /** `75.0%`, or `—` without an actual to compare. */
  completion: string;
  /** `at least` / `at most`. */
  direction: string;
  title: string;
}

/**
 * The Target column of a row: the active target covering the To week (`toWeekDate`), its completion against the
 * range figure the KPI is compared by (`completionBasis`), and a tooltip spelling it out. `null` without a target.
 */
export const targetDisplay = (
  kpi: Pick<TrackerKpi, 'name' | 'unit' | 'currency' | 'valueType'>,
  targets: readonly TrackerTarget[],
  toWeekDate: string | null,
  summary: RangeSummary,
): TargetDisplay | null => {
  const target = toWeekDate === null ? null : applicableTarget(targets, toWeekDate);

  if (!target || !isNumericUnit(kpi.unit)) {
    return null;
  }

  const basis = completionBasis(kpi);
  const actual = completionActual(summary, basis);
  const completion = formatCompletion(computeCompletion(actual, target.value, target.direction));
  const value = formatValue(kpi.unit, target.value, kpi.currency);
  const direction = DIRECTION_TEXT[target.direction];

  return {
    value,
    completion,
    direction,
    title:
      `${target.name}: ${direction} ${value} · ${weekDateLabel(target.fromWeekDate)} → ${weekDateLabel(target.toWeekDate)}\n` +
      `Completion ${completion}, comparing the ${BASIS_TEXT[basis]} (${formatValue(kpi.unit, actual, kpi.currency)}) with the target.`,
  };
};

/** Child KPIs by parent id, in grid order (the hierarchy is display-only; Phase 8 adds per-model children). */
export const childrenByParent = <TKpi extends Pick<TrackerKpi, 'id' | 'parentKpiId'>>(kpis: readonly TKpi[]): Map<string, TKpi[]> => {
  const children = new Map<string, TKpi[]>();

  for (const kpi of kpis) {
    if (kpi.parentKpiId !== null) {
      children.set(kpi.parentKpiId, [...(children.get(kpi.parentKpiId) ?? []), kpi]);
    }
  }

  return children;
};

export interface ChildrenSumHint {
  /** `120.0` — the children's sum. */
  children: string;
  /** `118.0`, or `—` when the parent is blank that week. */
  parent: string;
}

/** Units whose weekly values add up: a parent total and its parts. Rates, durations and ratios never do. */
const ADDITIVE_UNITS: ReadonlySet<Unit> = new Set(['NUMBER', 'CURRENCY']);

/**
 * The soft marker on a parent KPI's cell (Phase 8): when that week its children of the same unit and currency hold
 * values and their sum differs from the parent's value, the texts of "Children add up to X, parent shows Y"; `null`
 * otherwise (no child value, a non-additive unit, or the figures agree). Never blocking — the totals stay manual.
 */
export const childrenSumHint = (
  parent: UnitOf,
  parentValue: CellValue,
  children: readonly (UnitOf & { value: CellValue })[],
): ChildrenSumHint | null => {
  if (!ADDITIVE_UNITS.has(parent.unit)) {
    return null;
  }

  const values = children
    .filter((child) => child.unit === parent.unit && child.currency === parent.currency)
    .map((child) => child.value)
    .filter((value): value is number => typeof value === 'number');

  if (values.length === 0) {
    return null;
  }

  const sum = values.reduce((total, value) => total + value, 0);
  const shown = typeof parentValue === 'number' ? parentValue : null;
  const unit = parent.unit as 'NUMBER' | 'CURRENCY';

  if (shown !== null && Math.abs(sum - shown) <= 1e-9 * Math.max(1, Math.abs(sum))) {
    return null;
  }

  return {
    children: formatValue(unit, sum, parent.currency),
    parent: shown === null ? BLANK_DISPLAY : formatValue(unit, shown, parent.currency),
  };
};

/**
 * The name a child row shows under its parent: without the parent's name when it starts with it (`Vehicles sold —
 * Tiggo 4 HEV` under `Vehicles sold` reads `Tiggo 4 HEV`), so the per-model rows stay legible in the narrow KPI
 * column; the full name stays in the tooltip.
 */
export const childRowName = (name: string, parentName: string | null): string => {
  if (parentName === null) {
    return name;
  }

  const match = new RegExp(`^${parentName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[—–:-]\\s*(.+)$`, 'i').exec(name);

  return match?.[1] ?? name;
};
